/**
 * @marshal/brand — single source of truth for the SaaS brand identity.
 *
 * The working name is "Marshal" (placeholder). The WASC committee will pick
 * the final name. When they do, the swap is a 15-minute env-var change:
 *
 *   1. Update env vars: BRAND_NAME, BRAND_DOMAIN, BRAND_FROM_EMAIL, etc.
 *   2. Drop new logo / favicon assets into apps/web/public/brand/<newname>/
 *   3. Update the defaults in this file
 *   4. Push.
 *
 * Rules of brand-discipline (CI lint should enforce):
 *   - NEVER hardcode the brand name in any UI string, email template, or copy.
 *   - Always reference `brand.name`, `brand.domain`, etc.
 *   - Domain references in OAuth callbacks, email-from headers, and social
 *     meta tags all pull from this object.
 *   - Database stores zero brand name references. Tenant data is brand-agnostic.
 */

export interface Brand {
  name: string;
  domain: string;
  tagline: string;
  primaryColor: string;
  logoUrl: string;
  email: {
    from: string;
    support: string;
  };
  social: {
    x?: string;
    instagram?: string;
  };
}

/** Short club label when DB name is unavailable (first tenant default). */
export const DEFAULT_CLUB_SHORT_NAME = 'WASC';

/**
 * Resend / RFC 5322 From header with display name.
 * e.g. "WASC via Marshal Swim CRM <hello@marshal-swim.co.nz>"
 */
export function formatEmailFrom(brand: Brand, clubName?: string | null): string {
  const club = clubName?.trim();
  const displayName = club ? `${club} via ${brand.name}` : brand.name;
  const addr = brand.email.from.trim();
  if (addr.includes('<') && addr.includes('>')) return addr;
  return `${displayName} <${addr}>`;
}

export function clubDisplayName(clubName?: string | null, fallback = DEFAULT_CLUB_SHORT_NAME): string {
  return clubName?.trim() || fallback;
}

const env = (key: string, fallback: string): string => {
  // Works in Cloudflare Workers (globalThis), Node, and Vite (import.meta.env).
  // Vite-style: prefer import.meta.env at build time.
  const fromImportMeta =
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string | undefined> }).env?.[key];
  if (fromImportMeta) return fromImportMeta;

  // Node / Workers — read process.env via globalThis so we don't need @types/node here.
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  const fromProcess = proc?.env?.[key];
  if (fromProcess) return fromProcess;

  return fallback;
};

/**
 * The single brand object. Import this anywhere the brand surfaces in code:
 *   import { brand } from '@marshal/brand';
 *   <h1>Welcome to {brand.name}</h1>
 */
export const brand: Brand = {
  name: env('BRAND_NAME', 'Marshal'),
  domain: env('BRAND_DOMAIN', 'marshal.local'),
  tagline: env('BRAND_TAGLINE', 'Run the club, not the spreadsheet.'),
  primaryColor: env('BRAND_PRIMARY_COLOR', '#0d4a8a'),
  logoUrl: env('BRAND_LOGO_URL', '/brand/marshal/logo.webp'),
  email: {
    from: env('BRAND_FROM_EMAIL', 'hello@marshal-swim.co.nz'),
    support: env('BRAND_SUPPORT_EMAIL', 'support@marshal-swim.co.nz'),
  },
  social: {
    x: env('BRAND_X', ''),
    instagram: env('BRAND_INSTAGRAM', ''),
  },
};

/**
 * For Cloudflare Workers, where env is bound at request time, use this builder
 * inside a request handler:
 *
 *   app.get('/', (c) => {
 *     const b = brandFromEnv(c.env);
 *     return c.html(`<h1>${b.name}</h1>`);
 *   });
 */
export interface BrandEnv {
  BRAND_NAME?: string;
  BRAND_DOMAIN?: string;
  BRAND_TAGLINE?: string;
  BRAND_PRIMARY_COLOR?: string;
  BRAND_LOGO_URL?: string;
  BRAND_FROM_EMAIL?: string;
  BRAND_SUPPORT_EMAIL?: string;
  BRAND_X?: string;
  BRAND_INSTAGRAM?: string;
}

export const brandFromEnv = (workerEnv: BrandEnv): Brand => ({
  name: workerEnv.BRAND_NAME ?? 'Marshal',
  domain: workerEnv.BRAND_DOMAIN ?? 'marshal.local',
  tagline: workerEnv.BRAND_TAGLINE ?? 'Run the club, not the spreadsheet.',
  primaryColor: workerEnv.BRAND_PRIMARY_COLOR ?? '#0d4a8a',
  logoUrl: workerEnv.BRAND_LOGO_URL ?? '/brand/marshal/logo.webp',
  email: {
    from: workerEnv.BRAND_FROM_EMAIL ?? 'hello@marshal-swim.co.nz',
    support: workerEnv.BRAND_SUPPORT_EMAIL ?? 'support@marshal-swim.co.nz',
  },
  social: {
    x: workerEnv.BRAND_X,
    instagram: workerEnv.BRAND_INSTAGRAM,
  },
});
